/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */
import { YospaceAdsPlaybackPolicy } from './YospaceAdsPlaybackPolicy';
import { AdPlaybackPolicy, PlaybackAutoSeekMode, PlaybackFastForwardMode } from '@quickplay/player-interface';
import { YospaceAdsPlayer } from '../../../YospaceAdsPlayer';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';

const TAG_NAME = 'FL_YOSPACE_PLAYBACK_POLICY';

export class DefaultAdsPlaybackPolicy implements YospaceAdsPlaybackPolicy {
  /**
   * Provides access to this policy's playback mode.
   * @property {PlaybackMode} playbackMode
   */
  playbackMode: any;
  /**
   * Provides access to the Yospace SDK.
   * @property {any} yospaceSDK
   */
  yospaceSDK: any;
  adPlaybackPolicy: AdPlaybackPolicy;
  /**
   * Provides access to the logger.
   * @property {Logger} logger
   */
  private logger: Logger;

  private internalPlayer: YospaceAdsPlayer | undefined;
  private adBreakQueueToPlay: any;
  private destinationPlayhead?: number;

  constructor(adPlaybackPolicy: AdPlaybackPolicy, yospaceSDK: any, logger?: Logger) {
    this.yospaceSDK = yospaceSDK;
    this.adPlaybackPolicy = adPlaybackPolicy;
    this.playbackMode = null;
    this.internalPlayer = undefined;
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
  }

  setPlaybackMode(mode: any): void {
    this.playbackMode = mode;
  }

  setPlayer(player: YospaceAdsPlayer): void {
    this.internalPlayer = player;
  }

  canChangeVolume(): boolean {
    return true;
  }

  canClickThrough(): boolean {
    return true;
  }

  canPause(): boolean {
    return this.playbackMode !== this.yospaceSDK.PlaybackMode.LIVE;
  }

  canResize(): boolean {
    return true;
  }

  canResizeCreative(): boolean {
    return true;
  }

  canSkip(playhead: any, timeline: any, _duration: any): number {
    if (this.playbackMode !== this.yospaceSDK.PlaybackMode.LIVE) {
      // We can never skip live adverts
      return -1;
    } else {
      const ad = this.isInAdvert(playhead, timeline);
      if (!ad) {
        return -1;
      }

      let skipOffset = ad.getSkipOffset();
      if (skipOffset === -1) {
        // We can only skip if the advert allows it
        return -1;
      }

      if (!ad.isActive()) {
        // We can skip an inactive advert
        skipOffset = 0;
      } else if (skipOffset >= 0) {
        // Calculate the difference between offset and playhead, unless negative
        skipOffset = Math.max(0, ad.getStart() + skipOffset - playhead);
      }

      return skipOffset;
    }
  }

  canStop(): boolean {
    return true;
  }

  closestActiveBreakPriorTo(position: any, timeline: any): any {
    let closest = null;
    for (let i = 0; i < timeline.length; ++i) {
      const br = timeline[i];
      if (br.getStart() + br.getDuration() < position && br.isActive()) {
        closest = br;
      }
    }

    return closest;
  }

  didSeek(from: any, to: any, timeline: any): void {
    this.logger.log(`[AD POLICY] Did seek from ${from} to ${to}`, timeline);
    // If we sought forward in a VOD/NLSO stream, disable all adbreaks we sought past
    /*if (this.playbackMode !== this.yospaceSDK.PlaybackMode.LIVE && from < to) {
      this.setInactiveAllAdBreaksBetween(from, to, timeline);
    }*/
  }

  isInAdvert(playhead: any, timeline: any): any {
    for (let i = 0; i < timeline.length; ++i) {
      const br = timeline[i];
      if (br.getStart() <= playhead && playhead < br.getStart() + br.getDuration()) {
        for (let j = 0; j < br.getAdverts.length; ++j) {
          const ad = br.getAdverts()[j];
          if (ad.getStart() <= playhead && playhead < ad.getStart() + ad.getDuration()) {
            return ad;
          }
        }
      }
    }

    return null;
  }

  didSkip(from: any, to: any, timeline: any): any {
    this.logger.log(`[AD POLICY] Did skip from ${from} to ${to}`, timeline);
    //this.didSeek(from, to, timeline);
  }

  setInactiveAllAdBreaksBetween(start: any, end: any, timeline: any): void {
    for (let i = 0; i < timeline.length; ++i) {
      const br = timeline[i];
      if (br.isActive() && br.getStart() >= start && br.getStart() + br.getDuration() <= end) {
        br.setInactive();
      }
    }
  }

  willSeekTo(position: any, timeline: any): number {
    if (this.playbackMode === this.yospaceSDK.PlaybackMode.LIVE) {
      // can never skip live adverts
      return position;
    }

    if (this.internalPlayer && this.internalPlayer.currentTime) {
      return position;
    } else {
      return this.initialSeekPosition(position, timeline);
    }
  }

  evaluateSeek(adBreaks: any[]): void {
    if (!this.internalPlayer) {
      return;
    }

    if (this.adBreakQueueToPlay && this.adBreakQueueToPlay.length) {
      const nextAdStartTime = this.adBreakQueueToPlay[0].getStart();

      this.adBreakQueueToPlay.splice(0, 1);

      this.internalPlayer.seek(this.getPlayheadPositionWithoutAds(nextAdStartTime, adBreaks));

      return;
    }

    if (this.destinationPlayhead) {
      // Developer Note: YospacePlayer seek method expect the seek position that didn't include ads.
      // So we compute the actual seek position by eliminate ads duration before the destinationPlayhead.
      this.internalPlayer.seek(this.getPlayheadPositionWithoutAds(this.destinationPlayhead, adBreaks) / 1000);
      this.destinationPlayhead = undefined;
    }
  }

  initialSeekPosition(playheadMs: number, timeline: any[]): number {
    if (!timeline.length) {
      return playheadMs;
    }

    //const adBreaks = this.getAdBreaks(0, playheadMs, timeline, ['preroll', 'midroll']);
    const adBreaks = this.getAdBreaks(0, playheadMs, timeline);

    if (!adBreaks.length) {
      return playheadMs;
    }

    const midrollAdBreaks = adBreaks.filter((adBreak: any) => adBreak.getPosition() === 'midroll');

    switch (this.adPlaybackPolicy.autoSeekMode) {
      case PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL: {
        return playheadMs;
      } // ends case AUTO_SEEK_SKIP_ALL
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL: {
        //if (adBreaks[0].getPosition() === 'preroll') {
        if (adBreaks[0].getStart() === 0) {
          this.destinationPlayhead = playheadMs;
          return adBreaks[0].getStart();
        }
        return playheadMs;
      } // ends case AUTO_SEEK_PLAY_PREROLL
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_MIDROLL: {
        return this.handleAutoSeekPlayMidroll(playheadMs, midrollAdBreaks);
      } // ends case AUTO_SEEK_PLAY_MIDROLL
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_ALL: {
        //const prerollAdBreak = adBreaks[0].getPosition() === 'preroll' ? adBreaks[0] : undefined;
        const prerollAdBreak = adBreaks[0].getStart() === 0 ? adBreaks[0] : undefined;

        if (prerollAdBreak) {
          this.handleAutoSeekPlayAllOnPrerollAdBreak(midrollAdBreaks);

          this.destinationPlayhead = playheadMs;

          return prerollAdBreak.getStart();
        } else {
          return this.handleAutoSeekPlayAllOnNoPrerollAdBreak(playheadMs, midrollAdBreaks);
        } // if
      } // ends case AUTO_SEEK_PLAY_ALL
    }

    return playheadMs;
  }

  private handleAutoSeekPlayMidroll(playheadMs: number, midrollAdBreaks: any[]): number {
    switch (this.adPlaybackPolicy.fastForwardMode) {
      case PlaybackFastForwardMode.TRICK_FW_PLAY_FIRST: {
        if (midrollAdBreaks.length) {
          this.destinationPlayhead = playheadMs;
          return midrollAdBreaks[0].getStart();
        }
        return playheadMs;
      }
      case PlaybackFastForwardMode.TRICK_FW_PLAY_LAST: {
        if (midrollAdBreaks.length) {
          this.destinationPlayhead = playheadMs;
          return midrollAdBreaks[midrollAdBreaks.length - 1].getStart();
        }
        return playheadMs;
      }
      default: {
        if (midrollAdBreaks.length) {
          this.destinationPlayhead = playheadMs;

          const firstAdBreak = midrollAdBreaks.splice(0, 1)[0];

          this.adBreakQueueToPlay = midrollAdBreaks;

          if (firstAdBreak) {
            return firstAdBreak.getStart();
          }
        }
        return playheadMs;
      }
    }
  }

  private handleAutoSeekPlayAllOnPrerollAdBreak(midrollAdBreaks: any[]): void {
    switch (this.adPlaybackPolicy.fastForwardMode) {
      case PlaybackFastForwardMode.TRICK_FW_PLAY_FIRST: {
        const firstAdBreak = midrollAdBreaks[0];
        this.adBreakQueueToPlay = [firstAdBreak];
        break;
      }
      case PlaybackFastForwardMode.TRICK_FW_PLAY_LAST: {
        const lastAdBreak = midrollAdBreaks[midrollAdBreaks.length - 1];
        this.adBreakQueueToPlay = [lastAdBreak];
        break;
      }
      default: {
        this.adBreakQueueToPlay = midrollAdBreaks;
      }
    }
  }

  private handleAutoSeekPlayAllOnNoPrerollAdBreak(playheadMs: number, midrollAdBreaks: any[]): number {
    switch (this.adPlaybackPolicy.fastForwardMode) {
      case PlaybackFastForwardMode.TRICK_FW_PLAY_FIRST: {
        const firstAdBreak = midrollAdBreaks[0];
        this.destinationPlayhead = playheadMs;
        return firstAdBreak.getStart();
      }
      case PlaybackFastForwardMode.TRICK_FW_PLAY_LAST: {
        const lastAdBreak = midrollAdBreaks[midrollAdBreaks.length - 1];
        this.destinationPlayhead = playheadMs;
        return lastAdBreak.getStart();
      }
      default: {
        const firstAdBreak = midrollAdBreaks[0];
        this.destinationPlayhead = playheadMs;
        midrollAdBreaks.splice(0, 1);
        this.adBreakQueueToPlay = midrollAdBreaks;
        return firstAdBreak.getStart();
      }
    }
  }

  private getPlayheadPositionWithoutAds(playheadMs: number, adBreaks: any[]): number {
    const discountTimeInterval = adBreaks
      .filter((adBreak: any) => adBreak.getStart() < playheadMs)
      .map((adBreak: any) => adBreak.getDuration())
      .reduce((accumulator: number, advertBreakDuration: number) => accumulator + advertBreakDuration);

    return playheadMs - discountTimeInterval;
  }

  private getAdBreaks(startPlayheadMs: number, endPlayheadMs: number, timeline: any[], positions?: string[]): any[] {
    const betweenAdBreaks: any[] = [];

    timeline.forEach((adBreak: any) => {
      if (
        (adBreak.getDuration() > 0 &&
          positions &&
          positions.includes(adBreak.getPosition()) &&
          adBreak.getStart() >= startPlayheadMs &&
          adBreak.getStart() <= endPlayheadMs) ||
        (adBreak.getDuration() > 0 &&
          (adBreak.getStart() === 0 || (adBreak.getStart() >= startPlayheadMs && adBreak.getStart() <= endPlayheadMs)))
      ) {
        betweenAdBreaks.push(adBreak);
      }
    });

    return betweenAdBreaks;
  }
}
